import React, { useEffect, useRef } from 'react';

import { colorWithAlpha } from '../../../common/color';

function clampNumber(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function formatPointerTime(totalMinutes) {
  const normalized = clampNumber(Math.round(Number(totalMinutes) || 0), 0, 24 * 60);
  if (normalized >= 24 * 60) {
    return '24:00';
  }

  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function isInteractiveTimelineTarget(target) {
  return Boolean(target?.closest?.([
    '.task-block',
    '.workflow-occurrence-track',
    '.workflow-occurrence-track-label',
    '.workflow-row-drag-handle',
    '.timeline-context-menu',
    'button',
    'a',
    'input',
    'select',
    'textarea',
    '[role="button"]',
  ].join(',')));
}

export default function TimelineHeader({
  hours = [],
  zoom,
  widthPx,
  startHour = 0,
  endHour = 24,
  baseScale = 4,
  timeToPixels,
  hoverState = null,
  scrollRef = null,
  contentRef = null,
}) {
  const pointerTimeLabelRef = useRef(null);
  const hoverMarkers = hoverState?.markers ?? [];
  const markerDistance = hoverMarkers.length >= 2
    ? Math.abs(
      timeToPixels(hoverMarkers[1].minutes, zoom)
      - timeToPixels(hoverMarkers[0].minutes, zoom)
    )
    : Infinity;
  const hasTightMarkers = markerDistance < 112;
  const showCombinedLabel = hoverMarkers.length >= 2 && hasTightMarkers;

  useEffect(() => {
    const scrollEl = scrollRef?.current;
    const contentEl = contentRef?.current;
    const labelEl = pointerTimeLabelRef.current;
    if (!scrollEl || !contentEl || !labelEl) {
      return undefined;
    }

    let frameId = null;
    let latestEvent = null;

    function hidePointerTime() {
      labelEl.classList.remove('is-visible', 'is-near-start', 'is-near-end');
    }

    function updatePointerTime(event) {
      if (!event || event.pointerType === 'touch' || isInteractiveTimelineTarget(event.target)) {
        hidePointerTime();
        return;
      }

      const rect = contentEl.getBoundingClientRect();
      const x = clampNumber(event.clientX - rect.left, 0, widthPx);
      const minutes = clampNumber(
        (startHour * 60) + (x / (baseScale * zoom)),
        startHour * 60,
        endHour * 60,
      );

      labelEl.textContent = formatPointerTime(minutes);
      labelEl.style.left = `${x}px`;
      labelEl.classList.toggle('is-near-start', x < 44);
      labelEl.classList.toggle('is-near-end', x > widthPx - 44);
      labelEl.classList.add('is-visible');
    }

    function schedulePointerTimeUpdate(event) {
      latestEvent = event;
      if (frameId !== null) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null;
        updatePointerTime(latestEvent);
      });
    }

    scrollEl.addEventListener('pointermove', schedulePointerTimeUpdate);
    scrollEl.addEventListener('pointerleave', hidePointerTime);
    scrollEl.addEventListener('scroll', hidePointerTime, { passive: true });

    return () => {
      if (frameId !== null) {
        window.cancelAnimationFrame(frameId);
      }
      scrollEl.removeEventListener('pointermove', schedulePointerTimeUpdate);
      scrollEl.removeEventListener('pointerleave', hidePointerTime);
      scrollEl.removeEventListener('scroll', hidePointerTime);
    };
  }, [baseScale, contentRef, endHour, scrollRef, startHour, widthPx, zoom]);

  return (
    <div className="timeline-header" style={{ width: `${widthPx}px` }}>
      <div
        ref={pointerTimeLabelRef}
        className="timeline-pointer-time-label"
        aria-hidden="true"
      />

      {hours.map((m) => {
        const absMin = startHour * 60 + m;
        const h = Math.floor(absMin / 60);
        const min = absMin % 60;
        const label = `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;

        return (
          <div
            key={label}
            className="timeline-hour"
            style={{ left: `${timeToPixels(absMin, zoom)}px` }}
          >
            {label}
          </div>
        );
      })}

      {showCombinedLabel ? (
        <div
          className="timeline-hover-marker-label timeline-hover-marker-label-combined"
          style={{
            left: `${(
              timeToPixels(hoverMarkers[0].minutes, zoom)
              + timeToPixels(hoverMarkers[1].minutes, zoom)
            ) / 2}px`,
            '--timeline-hover-marker-color': colorWithAlpha(
              hoverState.color,
              0.8,
              'rgba(255, 255, 255, 0.82)'
            ),
            '--timeline-hover-marker-pill-bg': colorWithAlpha(
              hoverState.color,
              0.18,
              'rgba(255, 255, 255, 0.12)'
            ),
          }}
        >
          <span className="timeline-hover-marker-label-pill">
            {`${hoverMarkers[0].timeText} - ${hoverMarkers[1].timeText}`}
          </span>
        </div>
      ) : hoverMarkers.map((marker, index) => {
        const tightClass = hasTightMarkers
          ? (index === 0 ? 'is-tight-start' : 'is-tight-end')
          : '';

        return (
          <div
            key={`${hoverState.taskId}-${marker.key}`}
            className={`timeline-hover-marker-label timeline-hover-marker-label-${marker.type} ${tightClass}`.trim()}
            style={{
              left: `${timeToPixels(marker.minutes, zoom)}px`,
              '--timeline-hover-marker-color': colorWithAlpha(
                hoverState.color,
                0.8,
                'rgba(255, 255, 255, 0.82)'
              ),
              '--timeline-hover-marker-pill-bg': colorWithAlpha(
                hoverState.color,
                0.18,
                'rgba(255, 255, 255, 0.12)'
              ),
            }}
          >
            <span className="timeline-hover-marker-label-pill">
              {marker.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
